/**
 * Coordinates are in the 1024×1536 plate shared by the three portraits.
 * The mug PNG is 1254×1254 with transparent padding, so its box is larger
 * than the cream ceramic (opaque about 11.6%–92.7% across, 11.6%–90.8% down).
 *
 * Shoe soles land at y1436 on the sipping and working plates. The box below
 * puts the ceramic base on that line, in front of her left shoe.
 */
export const HERO_SIZE = { width: 1024, height: 1536 } as const;
export const MUG_PNG = { width: 1254, height: 1254 } as const;

export type HeroRect = { x: number; y: number; w: number; h: number };

/**
 * Floor mug is 20% larger than the original 196px box. It grows from the
 * ceramic base and the ceramic's horizontal center, so the base stays on
 * the shoe line and the cup does not drift across the floor.
 */
const MUG_CERAMIC = { left: 0.116, right: 0.927, bottom: 0.908 } as const;
const MUG_BASE = { x: 16, y: 1262, size: 196 } as const;
const MUG_SCALE = 1.2;
const mugCenter =
  MUG_BASE.x + ((MUG_CERAMIC.left + MUG_CERAMIC.right) / 2) * MUG_BASE.size;
const mugBaseY = MUG_BASE.y + MUG_CERAMIC.bottom * MUG_BASE.size;
const mugSize = MUG_BASE.size * MUG_SCALE;

export const HERO_ANCHORS = {
  mug: {
    x: mugCenter - ((MUG_CERAMIC.left + MUG_CERAMIC.right) / 2) * mugSize,
    y: mugBaseY - MUG_CERAMIC.bottom * mugSize,
    w: mugSize,
    h: mugSize,
  },
} satisfies Record<string, HeroRect>;

/** Battery outline from battery-overlay-template.svg, in mug-PNG space. */
export const MUG_BATTERY = {
  x: 382 / MUG_PNG.width,
  y: 621 / MUG_PNG.height,
  w: 382 / MUG_PNG.width,
  h: 169 / MUG_PNG.height,
} as const;

/** Opening ellipse from coffee-interior-mask.svg, in mug-PNG space. */
export const COFFEE_OPENING = {
  cx: 530 / MUG_PNG.width,
  cy: 238 / MUG_PNG.height,
  rx: 337 / MUG_PNG.width,
  ry: 48 / MUG_PNG.height,
} as const;

export function heroRectToPercent(rect: HeroRect) {
  return {
    x: rect.x / HERO_SIZE.width,
    y: rect.y / HERO_SIZE.height,
    w: rect.w / HERO_SIZE.width,
    h: rect.h / HERO_SIZE.height,
  };
}

/**
 * Streamless carafe, 1024×901. The spout tip is the rightmost opaque pixel.
 * Rendered width is 430 hero pixels, about 42% larger than the 302px pot,
 * so it stays in proportion with the portrait plates. The source PNG is unchanged.
 *
 * CARAFE_SPOUT_AXIS is the lip's built-in downward tilt. Rotation is clockwise
 * around the spout so the lip points down into the mug and the stream falls
 * nearly straight, slightly left of the opening.
 */
export const CARAFE_PNG = { width: 1024, height: 901 } as const;
export const CARAFE_SPOUT = { x: 985 / 1024, y: 373 / 901 } as const;
const CARAFE_SPOUT_AXIS = 10.75;
const CARAFE_WIDTH = 168 * 3 * 0.75 * 0.8 * 1.42;
/** Ribbon thickness kept from the earlier short stream. Length may change; width does not. */
const STREAM_WIDTH = 151.327 * (724 / 2172);

/**
 * Spout offset from the mug opening, in hero pixels, plus a scale of the
 * shared carafe width. Desktop reaches in from the left of the column.
 * Mobile keeps the same width: the spout sits above the opening and a
 * little to its right so the tilted pot stays inside the plate, over her
 * clothes, with the lip aimed down into the mug.
 */
const CARAFE_PLACEMENT = {
  desktop: { x: -18, y: -130, scale: 1 },
  mobile: { x: 96, y: -160, scale: 1 },
} as const;

/** Hero-pixel pose. Spout is anchored to the live mug opening. */
export function carafePose(variant: keyof typeof CARAFE_PLACEMENT = "desktop") {
  const placed = CARAFE_PLACEMENT[variant];
  const mug = HERO_ANCHORS.mug;
  const opening = {
    x: mug.x + COFFEE_OPENING.cx * mug.w,
    y: mug.y + COFFEE_OPENING.cy * mug.h,
  };
  const width = CARAFE_WIDTH * placed.scale;
  const height = width * (CARAFE_PNG.height / CARAFE_PNG.width);
  const spout = {
    x: opening.x + placed.x,
    y: opening.y + placed.y,
  };
  const dx = opening.x - spout.x;
  const dy = opening.y - spout.y;
  const streamAngle = (Math.atan2(dy, dx) * 180) / Math.PI - 90;
  const aim = (Math.atan2(dy, dx) * 180) / Math.PI;
  return {
    tilt: aim - CARAFE_SPOUT_AXIS,
    spout,
    opening,
    streamAngle,
    box: {
      x: spout.x - CARAFE_SPOUT.x * width,
      y: spout.y - CARAFE_SPOUT.y * height,
      w: width,
      h: height,
    },
    stream: {
      x: spout.x,
      y: spout.y,
      h: Math.hypot(dx, dy),
      w: STREAM_WIDTH,
      angle: streamAngle,
    },
  };
}

export function heroPointToPercent(x: number, y: number) {
  return { x: x / HERO_SIZE.width, y: y / HERO_SIZE.height };
}
